import { PerAdultLabel } from '@/src/components/common/srp/perAdultLabel'
import PremiumBadge from '@/src/components/mobile/srp/SRPFareFamily/badge'
import useSearchResults from '@/src/hooks/srp/useSearchResults'
import { Box, Card, Radio, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import { Plan } from '../types'
import ExclusiveBenefitNote from './ExclusiveBenefitNote'
import FareBenefitRow from './FareBenefitRow'

export default function FarePlanCard({
    plan,
    isSelected,
    onSelect,
    cardRef,
    registerRef,
    rowHeights,
    colIndex,
    isExclusiveBenefitsAvailable
}: {
    plan: Plan
    isSelected: boolean
    onSelect: () => void
    cardRef?: React.Ref<HTMLDivElement>
    forcedHeight?: number | null
    benefitKeys: string[]
    registerRef: (colIndex: number, rowIndex: number) => (el: HTMLDivElement | null) => void
    rowHeights: number[]
    colIndex: number
    isExclusiveBenefitsAvailable: boolean
}) {
    const { selectedJourneyLeg } = useSearchResults()
    const t = useTranslations('common')
    return (
        <Card
            ref={cardRef}
            variant="outlined"
            onClick={onSelect}
            sx={theme => ({
                borderWidth: 2,
                borderColor: isSelected ? theme.palette.primary.main : 'grey.300',
                position: 'relative',
                cursor: 'pointer',
                display: 'grid',
                width: { sm: '240px', md: '265px', lg: '320px' },
                pt: 0,
                transition: 'all 0.3s ease',
                overflow: 'visible'
            })}
        >
            {plan.badge && <PremiumBadge sx={{ marginLeft: '10px' }} badgeText={plan.badge} />}

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                    width: '100%',
                    pb: '0.75rem',
                    pt: '1rem',
                    px: '1rem',
                    borderBottom: '1px solid #ccc'
                }}
            >
                <Box width="100%">
                    <Typography variant="subtitle1" fontWeight={500}>
                        {plan.title}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center" width="100%">
                        <Typography variant="h5" fontWeight={700}>
                            {!!plan.planPricePrefix && plan.planPricePrefix} {plan.displayPrice}{' '}
                            {plan.isPerPaxPricingDefined && <PerAdultLabel />}
                        </Typography>
                        {!!plan.showTotalRTPriceMessage && (
                            <Typography
                                variant="caption"
                                fontWeight={600}
                                // bgcolor={theme => theme.palette.primary.light}
                                color="#484747"
                                border={'1px solid #cac8c8'}
                                borderRadius={0.5}
                                px={0.5}
                            >
                                {t('roundTripTotalPrice')}
                            </Typography>
                        )}
                    </Box>
                </Box>
                <Radio checked={isSelected} sx={{ position: 'absolute', top: 3, right: 3 }} />
            </Box>

            {Object.entries(plan.benefitsText).map(([key, value], rowIndex) => {
                const isLastRow = key === 'exclusiveBenefits'
                const ref = registerRef(colIndex + 1, rowIndex)

                if (isLastRow && !isExclusiveBenefitsAvailable) return
                if (isLastRow && selectedJourneyLeg == 1) return

                return (
                    <Box
                        key={key}
                        ref={ref}
                        sx={{
                            minHeight: rowHeights[rowIndex] ?? 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '8px 12px',
                            borderTop: rowIndex !== 0 ? '1px dashed #ccc' : 'none',
                            boxSizing: 'border-box'
                        }}
                    >
                        {value &&
                            (isLastRow ? (
                                <ExclusiveBenefitNote note={value} />
                            ) : (
                                <FareBenefitRow label={key} text={value} plan={plan} />
                            ))}
                    </Box>
                )
            })}
        </Card>
    )
}
